// pages/delivery/DeliveryForm.tsx
import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Select,
  Input,
  Slider,
  Typography,
  Row,
  Col,
  Space,
  Modal,
  Form,
  Table,
  Spin,
  Flex,
} from "antd";
import moment from "moment";
import {
  useCreateSupplierMutation,
  useFetchSuppliersListQuery,
  useCreateDeliveryMutation,
  useFetchDeliveriesListQuery,
  useUpdateDeliveryCommentMutation,
} from "./delivery.service";
import { useTranslation } from "react-i18next";
import "./style.css";
import { toast } from "react-toastify";
import { useSelector } from "react-redux"; // ✅ ADD
import LocationSelector from "../../components/LocationSelector"; // ✅ ADD

const { Title, Text } = Typography;
const { Option } = Select;
const { Column } = Table;

export default function DeliveryForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.app.user); // ✅ ADD

  // ✅ Location state (like checklist)
  const [locationId, setLocationId] = useState(null);

  const [supplier, setSupplier] = useState("");
  const [invoiceRef, setInvoiceRef] = useState("");
  const [isIntact, setIsIntact] = useState(false);
  const [deliveryType, setDeliveryType] = useState("");
  const [temperature, setTemperature] = useState(0);
  const [comment, setComment] = useState("");
  const [supplierModalVisible, setSupplierModalVisible] = useState(false);
  const [commentModalDelivery, setCommentModalDelivery] = useState(null);
  const [commentDraft, setCommentDraft] = useState("");

  // ✅ Set initial location from user
  useEffect(() => {
    const locId = user?.activeLocationId || user?.restaurantId;
    setLocationId(locId);
  }, [user]);

  // ✅ Handle location change
  const handleLocationChange = (newLocationId) => {
    setLocationId(newLocationId);
    refetchDeliveries(); // ✅ Refetch data for new location
    refetchSuppliers();  // ✅ Refetch suppliers for new location
  };

  // ✅ Fetch deliveries with locationId (passed as parameter)
  const {
    data: deliveryData = { data: { deliveries: [] } },
    isLoading: isDeliveryLoading,
    isSuccess: isDeliverySuccess,
    refetch: refetchDeliveries,
  } = useFetchDeliveriesListQuery(locationId, {
    refetchOnMountOrArgChange: true,
    skip: !locationId,
  });

  const {
    data: { deliveries },
  } = deliveryData;

  // ✅ Fetch suppliers with location
  const {
    data: supplierData = { data: { supplieres: [] } },
    isLoading: isSupplierLoading,
    isSuccess: isSupplierSuccess,
    refetch: refetchSuppliers,
  } = useFetchSuppliersListQuery(locationId, {
    refetchOnMountOrArgChange: true,
    skip: !locationId,
  });

  const {
    data: { supplieres },
  } = supplierData;

  const [
    createSupplier,
    { isSuccess: isCreateSupplierSuccess, isLoading: isCreateSupplierLoading },
  ] = useCreateSupplierMutation();

  const [
    createDelivery,
    { isSuccess: isCreateDeliverySuccess, isLoading: isCreateDeliveryLoading },
  ] = useCreateDeliveryMutation();

  const [updateDeliveryComment, { isLoading: isCommentSaving }] =
    useUpdateDeliveryCommentMutation();

  const [form] = Form.useForm();

  const handleSubmit = () => {
    if (!locationId) {
      toast.error("Please select a location first");
      return;
    }

    if (isIntact === false && !comment.trim()) {
      toast.error("Please provide a comment when delivery is not intact");
      return;
    }

    createDelivery({
      supplierName: supplier,
      invoiceRef: invoiceRef,
      isIntact: isIntact,
      deliveryType: deliveryType,
      temperature: temperature,
      ...(comment.trim() ? { comment: comment.trim() } : {}),
    });
    setSupplier("");
    setInvoiceRef("");
    setIsIntact(false);
    setDeliveryType("");
    setTemperature(0);
    setComment("");
  };

  const handleAddSupplier = () => {
    form
      .validateFields()
      .then((values) => {
        createSupplier({
          businessName: values.businessName,
          contactName: values.contactName,
          telephone: values.telephone,
          email: values.email,
          address: values.address,
        });
        form.resetFields();
        setSupplierModalVisible(false);
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (isCreateSupplierSuccess) {
      toast.success("Supplier added!");
    }
  }, [isCreateSupplierLoading]);

  useEffect(() => {
    if (isCreateDeliverySuccess) {
      toast.success(t("Delivery submitted!"));
    }
  }, [isCreateDeliveryLoading]);

  const isLoading = isDeliveryLoading || isSupplierLoading;

  return (
    <Spin spinning={isLoading}>
      <Card
        className="incident-page"
        bordered={false}
        title={
          <Flex align="center" gap="middle" wrap>
            <Title level={4}>{t("DELIVERIES")}</Title>
            {/* ✅ Add LocationSelector like checklist */}
            <LocationSelector onLocationChange={handleLocationChange} />
          </Flex>
        }
        extra={<Text>{moment().format("dddd Do MMMM, h:mm A")}</Text>}
      >
        <Row gutter={[0, 20]} style={{ maxWidth: 400 }}>
          {/* Supplier Name with location-aware dropdown */}
          <Col span={24}>
            <Text strong>{t("NAME OF SUPPLIER")}</Text>
            <Select
              style={{ width: "100%" }}
              placeholder="Supplier dropdown"
              value={supplier}
              onChange={setSupplier}
              loading={isSupplierLoading}
            >
              {supplieres.map((s, i) => (
                <Option key={i} value={s.businessName}>
                  {s.businessName}
                </Option>
              ))}
            </Select>
          </Col>

          <Col span={24}>
            <Text strong>{t("DELIVERY INVOICE REF")}</Text>
            <Input
              placeholder="Enter invoice reference"
              value={invoiceRef}
              onChange={(e) => setInvoiceRef(e.target.value)}
            />
          </Col>

          <Col span={24}>
            <Text strong>
              {t("IS DELIVERY INTACT, IN DATE AND CORRECT QUANTITY?")}
            </Text>
            <Select
              style={{ width: "100%" }}
              placeholder="YES / NO dropdown"
              value={isIntact}
              onChange={setIsIntact}
            >
              <Option value={true}>{t("Yes")}</Option>
              <Option value={false}>{t("No")}</Option>
            </Select>
          </Col>

          <Col span={24}>
            <Text strong>{t("CHILLED, FROZEN OR AMBIENT")}</Text>
            <Select
              style={{ width: "100%" }}
              placeholder="chilled / frozen / ambient dropdown"
              value={deliveryType}
              onChange={setDeliveryType}
            >
              <Option value="Chilled">{t("Chilled")}</Option>
              <Option value="Frozen">{t("Frozen")}</Option>
              <Option value="Ambient">{t("Ambient")}</Option>
            </Select>
          </Col>

          <Col span={24}>
            <Text strong>
              {t("PROBE & RECORD TEMPERATURE OF CHILLED or FROZEN")}
            </Text>
            <Text type="secondary">
              {t("Chilled should be 2–8°C and Frozen should be -12 – -20°C.")}{" "}
              <br />
              {t("Reject if chilled is above 8°C or frozen is above -12°C.")}
            </Text>
            <div
              style={{
                fontSize: "1.5rem",
                textAlign: "center",
                marginTop: 10,
                fontWeight: "bold",
              }}
            >
              {temperature.toFixed(1)}°C
            </div>
            <Slider
              styles={{
                track: {
                  background: "transparent",
                },
                tracks: {
                  background: `rgb(109, 179, 63)`,
                },
              }}
              min={-30}
              max={120}
              step={0.1}
              value={temperature}
              onChange={setTemperature}
              tooltipVisible={false}
            />
          </Col>

          <Col span={24}>
            <Text strong>{t("Delivery ref / comment")}</Text>
            <Input.TextArea
              rows={2}
              placeholder="Add comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </Col>

          <Col span={24}>
            <Text strong>
              {t("DATE AND TIME WILL BE AUTOMATICALLY RECORDED")}
            </Text>
          </Col>

          <Col span={24}>
            <Space>
              <Button
                type="primary"
                style={{ background: "yellow", color: "#000" }}
                onClick={handleSubmit}
                loading={isCreateDeliveryLoading}
                disabled={!locationId}
              >
                {t("SUBMIT")}
              </Button>
              <Button
                style={{ background: "#1e1f63", color: "white" }}
                onClick={() => setSupplierModalVisible(true)}
              >
                {t("ADD SUPPLIER")}
              </Button>
            </Space>
          </Col>
        </Row>

        {/* ✅ Location-aware deliveries table */}
        <Table 
          dataSource={deliveries} 
          loading={isDeliveryLoading}
          locale={{ emptyText: locationId ? "No deliveries found" : "Please select a location" }}
        >
          <Column
            align="center"
            title={t("Date")}
            dataIndex={"createdAt"}
            render={(date) =>
              new Date(date)
                .toLocaleString("en-GB", {
                  year: "numeric",
                  month: "long",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })
                .replace(" at", ",")
            }
          />
          <Column
            align="center"
            title={t("Supplier Name")}
            dataIndex={"supplierName"}
          />
          <Column
            align="center"
            title={t("Invoice")}
            dataIndex={"invoiceRef"}
          />
          <Column
            align="center"
            title={t("Type")}
            dataIndex={"deliveryType"}
          />
          <Column
            align="center"
            title={t("Temperature")}
            dataIndex={"temperature"}
            render={(temperature) => `${temperature.toFixed(1)}°C`}
          />
          <Column
            align="center"
            title={t("Intact")}
            dataIndex={"isIntact"}
            render={(isIntact) => (isIntact ? "Yes" : "No")}
          />
          <Column
            align="center"
            title={t("Delivery ref / comment")}
            dataIndex={"comment"}
            render={(value, record) => {
              const text =
                typeof value === "string"
                  ? value
                  : value?.default || value?.en || "";
              return (
                <Button
                  type="link"
                  onClick={() => {
                    setCommentModalDelivery(record);
                    setCommentDraft(text);
                  }}
                >
                  {text.trim() ? text : "Add comment"}
                </Button>
              );
            }}
          />
          {/* ✅ Optional: Add Location column to show which location */}
          <Column
            align="center"
            title={t("Location")}
            dataIndex={"restaurantId"}
            render={(restaurantId) => restaurantId?.name || "N/A"}
          />
        </Table>

        <Modal
          title={t("Delivery ref / comment")}
          open={!!commentModalDelivery}
          onOk={async () => {
            const saved = commentDraft.trim();
            if (!saved || !commentModalDelivery?._id) {
              toast.error("Please enter a comment");
              return;
            }
            try {
              await updateDeliveryComment({
                id: commentModalDelivery._id,
                comment: saved,
              }).unwrap();
              toast.success("Comment saved");
              setCommentModalDelivery(null);
              setCommentDraft("");
            } catch (e) {
              toast.error("Could not save comment");
            }
          }}
          onCancel={() => {
            setCommentModalDelivery(null);
            setCommentDraft("");
          }}
          confirmLoading={isCommentSaving}
        >
          <Input.TextArea
            rows={3}
            value={commentDraft}
            onChange={(e) => setCommentDraft(e.target.value)}
            placeholder="Add comment"
          />
        </Modal>

        <Modal
          title={t("Add Supplier")}
          open={supplierModalVisible}
          onOk={handleAddSupplier}
          onCancel={() => setSupplierModalVisible(false)}
          okText={t("Add")}
          cancelText={t("Cancel")}
          confirmLoading={isCreateSupplierLoading}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="businessName"
              label={t("Business Name")}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="contactName"
              label={t("Contact Name")}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="telephone"
              label={t("Telephone")}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="email"
              label={t("Email")}
              rules={[{ required: true, type: "email" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="address"
              label={t("Address")}
              rules={[{ required: true }]}
            >
              <Input.TextArea rows={2} />
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </Spin>
  );
}